// frontend/src/services/authApi.js
// Phoenix/Guardian JWT auth via httpOnly cookies (T201/T203).
// No localStorage token reads — backend manages nd_access/nd_refresh cookies.
import axios from "axios";
import { API_BASE_URL } from "../apiConstants";

const AUTH_URL = API_BASE_URL.replace("/v1", "") + "/v1/auth";
const COOKIE_CONFIG = { withCredentials: true };

const handleApiError = (error, context = "operation") => {
  const data = error.response?.data;
  const validationDetails =
    data?.details && typeof data.details === "object"
      ? Object.entries(data.details)
          .map(([field, messages]) => {
            const text = Array.isArray(messages)
              ? messages.join(", ")
              : String(messages);
            return `${field.replaceAll("_", " ")}: ${text}`;
          })
          .join("; ")
      : "";
  const networkUnavailable =
    !error.response &&
    (error.code === "ERR_NETWORK" || error.message === "Network Error");
  const message =
    data?.errors?.detail ||
    data?.message ||
    validationDetails ||
    data?.error ||
    (networkUnavailable
      ? "Unable to reach the authentication service. Check your connection or contact your administrator, then try again."
      : "") ||
    error.message ||
    `Auth ${context} failed.`;
  const failure = new Error(message);
  // Callers need the status to tell "signed out" (401) from "throttled" (429).
  failure.status = error.response?.status;
  throw failure;
};

// ── Entered workspace (super admin impersonation) ───────────────────────────
// Inside a workspace entered from the account menu, the access cookie is the
// one-hour impersonation token and nd_refresh still belongs to the super
// admin. When that token lapses, a refresh signs the super admin back in as
// themselves, so the page reloads into their own workspace instead of
// replaying requests under the entered workspace's screens.
let impersonationActive = false;
export const setImpersonationActive = (active) => {
  impersonationActive = Boolean(active);
};
export const impersonationNav = {
  returnHome: () => window.location.assign("/dashboard"),
};

// ── Token refresh queue (prevents race conditions on 401) ────────────────────
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve();
  });
  failedQueue = [];
};

// Axios response interceptor — auto-refresh on 401 using cookie-based refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = originalRequest?.url?.includes("/auth/");
    const mayRefreshSession =
      !isAuthEndpoint || originalRequest?._refreshAuthSession === true;
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      mayRefreshSession
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Refresh uses nd_refresh cookie — no body payload needed
        await axios.post(`${AUTH_URL}/refresh`, {}, COOKIE_CONFIG);
        if (impersonationActive) {
          impersonationActive = false;
          impersonationNav.returnHome();
          // The page is leaving; nothing waits on these requests.
          return new Promise(() => {});
        }
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Never redirect guests away from public routes (landing, login,
        // register). A failed refresh on /  means "no session" for a
        // first-time visitor — the landing page must still render.
        const publicPaths = new Set([
          "/",
          "/login",
          "/register",
          "/reset-password",
          "/auth/oauth/callback",
        ]);
        const isPublicPath =
          publicPaths.has(window.location.pathname) ||
          window.location.pathname.startsWith("/invite/");
        if (!isPublicPath) {
          window.location.href = "/login";
        }
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// ── Auth API ─────────────────────────────────────────────────────────────────

const authApi = {
  login: async (email, password) => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/login`,
        { email, password },
        COOKIE_CONFIG,
      );
      // Backend sets nd_access + nd_refresh httpOnly cookies on success
      return response.data.data;
    } catch (error) {
      handleApiError(error, "login");
    }
  },

  register: async (userData) => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/register`,
        userData,
        COOKIE_CONFIG,
      );
      return response.data.data;
    } catch (error) {
      handleApiError(error, "register");
    }
  },

  logout: async () => {
    try {
      await axios.delete(`${AUTH_URL}/logout`, COOKIE_CONFIG);
    } catch (_) {
      // Always proceed — backend clears cookies; nothing to clean client-side
    }
  },

  me: async () => {
    try {
      // The session probe runs on every full page load. Once the 15-minute
      // access cookie has expired it must refresh from nd_refresh instead of
      // treating the 401 as a signed-out user.
      const response = await axios.get(`${AUTH_URL}/me`, {
        ...COOKIE_CONFIG,
        _refreshAuthSession: true,
      });
      return response.data;
    } catch (error) {
      handleApiError(error, "fetch profile");
    }
  },

  socketTicket: async () => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/socket-ticket`,
        {},
        {
          ...COOKIE_CONFIG,
          _refreshAuthSession: true,
        },
      );
      return response.data.data;
    } catch (error) {
      handleApiError(error, "real-time connection");
    }
  },

  refreshToken: async () => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/refresh`,
        {},
        COOKIE_CONFIG,
      );
      return response.data.data;
    } catch (error) {
      handleApiError(error, "refresh token");
    }
  },

  updateProfile: async (profileData) => {
    try {
      const response = await axios.put(
        `${AUTH_URL}/me`,
        profileData,
        COOKIE_CONFIG,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "update profile");
    }
  },

  changePassword: async ({
    current_password,
    password,
    password_confirmation,
  }) => {
    try {
      const response = await axios.put(
        `${AUTH_URL}/me/password`,
        {
          current_password,
          new_password: password,
          password_confirmation,
        },
        COOKIE_CONFIG,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "change password");
    }
  },

  requestPasswordReset: async (email) => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/password-reset`,
        { email },
        COOKIE_CONFIG,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "request password reset");
    }
  },

  // Public: which recovery paths this deployment really offers. Any failure
  // reads as "no email", so the sign-in page never promises mail it can't send.
  getAuthOptions: async () => {
    try {
      const response = await axios.get(`${AUTH_URL}/options`, {
        ...COOKIE_CONFIG,
        timeout: 5000,
      });
      const data = response.data?.data || {};
      return {
        emailDelivery: (data.emailDelivery ?? data.email_delivery) === true,
      };
    } catch {
      return { emailDelivery: false };
    }
  },

  // Sets a new password with the one-time token from a reset email or an
  // admin-issued reset link. The token goes in the body, not the URL path, so
  // it stays out of request logs.
  resetPassword: async (token, password) => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/reset-password`,
        { token, password },
        COOKIE_CONFIG,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "reset password");
    }
  },

  getInvitation: async (token) => {
    try {
      const response = await axios.get(
        `${AUTH_URL}/invitations/${encodeURIComponent(token)}`,
        COOKIE_CONFIG,
      );
      return response.data.data;
    } catch (error) {
      handleApiError(error, "invitation lookup");
    }
  },

  acceptInvitation: async (token, passwordData) => {
    try {
      const response = await axios.put(
        `${AUTH_URL}/invitations/${encodeURIComponent(token)}`,
        passwordData,
        COOKIE_CONFIG,
      );
      return response.data.data;
    } catch (error) {
      handleApiError(error, "invitation acceptance");
    }
  },
  // T203: exchange one-time OAuth code for httpOnly cookie session
  exchangeOAuthCode: async (code) => {
    try {
      const response = await axios.post(
        `${AUTH_URL}/oauth/exchange`,
        { code },
        COOKIE_CONFIG,
      );
      return response.data;
    } catch (error) {
      handleApiError(error, "OAuth code exchange");
    }
  },
};

export default authApi;
